/**
 * Channels' idle clock and draft store.
 *
 * One clock per app, built once at module load. The cookie is scoped to
 * samsurveyor.com wherever that is allowed, so Channels shares one clock with
 * Console, Designer and Explorer. Working in any one of them keeps the others
 * signed in.
 *
 * The clock is only ever started when sign-in is required. With sign-in off —
 * the local default, see config.js — there is no session, so there is nothing
 * to sign out of. Running a 30-minute timer against a screen that was never
 * signed in would lock a developer out of the machine the screen is built on,
 * which is exactly the mistake config.js exists to avoid.
 *
 * ── Why the imports are deep paths ──
 *
 * `@cloudsam/ui` also exports these, but its package index pulls in AppShell,
 * Panel, Button and the rest. Any panel importing this file would then drag the
 * whole component set into its own import graph, and its tests would have to
 * stand up an app shell they never render. These three modules are plain
 * JavaScript with no React in them, so a deep import costs nothing and keeps
 * the graph small.
 */

import { createIdleClock } from '@cloudsam/ui/dist/idle/idleClock';
import { createDraftStore } from '@cloudsam/ui/dist/idle/draftStore';
import { sessionCookieDomain } from '@cloudsam/ui/dist/idle/cookieDomain';
import { createHeartbeatSender } from '@cloudsam/ui/dist/idle/heartbeat';
import { getDbamBase } from './dbamBase';
import { REQUIRE_AUTH } from './config';

/** How long a person may be idle before the session ends. */
export const IDLE_LOCK_MINUTES = 30;

/** Where the Channels recovery drafts live inside the store. */
export const DRAFT_SCOPE = 'channels';

export const idleClock = createIdleClock({
    cookiePrefix: 'sam',
    cookieDomain: sessionCookieDomain(),
    // Tells sam-dbam a person is here, so it can enforce the same 30 minutes on
    // its own. Sent only when sign-in is required: with sign-in off there is no
    // session to report about, and the call would fail for a reason that has
    // nothing to do with this screen.
    onHeartbeat: REQUIRE_AUTH
        ? createHeartbeatSender({ url: () => `${getDbamBase()}/dbam/v1/me/heartbeat` })
        : null,
});

export const draftStore = createDraftStore();

/**
 * Who is signed in, for the panels that write recovery drafts.
 *
 * A draft is keyed by user id, so one person's unsaved work can never be
 * offered to another who signs in on the same machine. The panels that need it
 * sit three levels below the auth context, and threading a prop through every
 * one of them would couple screens to identity that otherwise have nothing to
 * do with it.
 *
 * This is the same shape the codebase already uses for the org and user headers
 * (`setOrgContext` in utils/channelsApi). AuthContext is the only writer.
 */
let currentUserId = '';

export function setDraftUserId(userId) {
    currentUserId = String(userId || '');
}

/** Empty when nobody is signed in. A draft is never written without an id. */
export function getDraftUserId() {
    return currentUserId;
}

export default idleClock;
